// API Endpoints (override via Vite env vars for SOLO/staging)
const env = (key: string) => (import.meta as any)?.env?.[key] as string | undefined;
const mode = (import.meta as any)?.env?.MODE as string | undefined;
const isProdMode = mode === 'production';

function resolveApiUrl(envKey: string, prodDefault: string, devDefault: string): string {
  const override = env(envKey);
  if (!isProdMode) return override ?? devDefault;
  if (!override) return prodDefault;
  // Safety guard: production builds must not point critical APIs to non-prod paths.
  return override.includes('/prod') ? override : prodDefault;
}

function envBool(key: string, defaultValue = false): boolean {
  const raw = env(key);
  if (raw == null) return defaultValue;
  return ['1', 'true', 'yes', 'on'].includes(String(raw).trim().toLowerCase());
}

export const FEATURE_FLAGS = {
  // When true: the backend consumes/refunds credits; frontend must NOT call deductCredits().
  SERVER_SIDE_CREDITS: envBool('VITE_SERVER_SIDE_CREDITS', true),
};

export const API_CONFIG = {
  RESEARCH_API: resolveApiUrl(
    'VITE_RESEARCH_API',
    'https://kohic3mczj.execute-api.us-east-1.amazonaws.com/prod',
    'https://14khltoh3m.execute-api.us-east-1.amazonaws.com/dev'
  ),
  SEARCH_API: resolveApiUrl(
    'VITE_SEARCH_API',
    'https://5tky7j6eu0.execute-api.us-east-1.amazonaws.com/prod',
    'https://k5nqap9rm4.execute-api.us-east-1.amazonaws.com/dev'
  ),
  ORCHESTRATOR_API: resolveApiUrl(
    'VITE_ORCHESTRATOR_API',
    'https://nmscuoy8pd.execute-api.us-east-1.amazonaws.com/prod/',
    'https://nmscuoy8pd.execute-api.us-east-1.amazonaws.com/dev/'
  ),
  ECOM_ORCHESTRATOR_API: resolveApiUrl(
    'VITE_ECOM_ORCHESTRATOR_API',
    'https://mcug2ucn8e.execute-api.us-east-1.amazonaws.com/prod',
    'https://mcug2ucn8e.execute-api.us-east-1.amazonaws.com/dev'
  ),
  BOOKING_API: resolveApiUrl(
    'VITE_BOOKING_API',
    'https://r1zvxytro5.execute-api.us-east-1.amazonaws.com/prod',
    'https://r1zvxytro5.execute-api.us-east-1.amazonaws.com/dev'
  ),
  GRADES_API: resolveApiUrl(
    'VITE_GRADES_API',
    'https://ox5so7n8oj.execute-api.us-east-1.amazonaws.com/prod',
    'https://ox5so7n8oj.execute-api.us-east-1.amazonaws.com/dev'
  ),
  USERS_API: resolveApiUrl(
    'VITE_USERS_API',
    'https://vjtbe739c2.execute-api.us-east-1.amazonaws.com/prod/',
    'https://27b09myq8l.execute-api.us-east-1.amazonaws.com/dev/'
  ),
  USERS_API_DEV:
    env('VITE_USERS_API_DEV') ?? 'https://27b09myq8l.execute-api.us-east-1.amazonaws.com/dev/',
  MESSENGER_API:
    env('VITE_MESSENGER_API') ??
    'https://rkn388vla2.execute-api.us-east-1.amazonaws.com/prod',
  MAKE_WEBHOOK_URL: env('VITE_MAKE_WEBHOOK_URL') ?? 'https://hook.us2.make.com/6y091g26sua7e454ihy1rtgopap9nhlo',
  CUSTOM_SCRAPE_API: env('VITE_CUSTOM_SCRAPE_API') ?? '',
  // Apify (should NOT be hardcoded in frontend code)
  // Provide via env:
  // - VITE_APIFY_API_TOKEN
  // - VITE_APIFY_ACTOR_ID
  APIFY_API_TOKEN: env('VITE_APIFY_API_TOKEN') ?? '',
  // Actor ID is not secret; keep a safe default and allow override.
  APIFY_ACTOR_ID: env('VITE_APIFY_ACTOR_ID') ?? 'A3cAPGpwBEG8RJwse',
  GUIDE_API:
    env('VITE_GUIDE_API') ??
    (isProdMode
      ? 'https://kohic3mczj.execute-api.us-east-1.amazonaws.com/prod/guide/advice'
      : 'https://14khltoh3m.execute-api.us-east-1.amazonaws.com/dev/guide/advice'),
};

// Credit costs for each scraping source
export const CREDIT_COSTS: Record<string, number> = {
  // Recruiter intelligence
  linkedin_360: 2,
  glassdoor: 3,
  consumer_intel: 2,
  crunchbase: 1,
  custom_scrape: 2,
  google_reviews: 2,
  g2_reviews: 2,
  trustpilot: 1,

  // Custom scrapes / socials
  linkedin_profile_only: 1,
  facebook: 1,
  instagram: 1,
  youtube: 1,
  tiktok: 1,
  x: 2,
  threads: 1,
  podcast: 2,
  website: 1,
};

// Helper function for API calls
export async function fetchAPI<T>(
  url: string,
  options?: RequestInit
): Promise<T> {
  const method = (options?.method || 'GET').toUpperCase();
  const shouldSetJson =
    method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
  const response = await fetch(url, {
    ...options,
    headers: {
      ...(shouldSetJson ? { 'Content-Type': 'application/json' } : {}),
      ...options?.headers,
    },
  });
  
  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  
  return response.json();
}
